/**
 * Browser Use Cloud hosted agents — the browser-automation backend.
 *
 * WHY THIS REPLACES A LOOP RATHER THAN A BROWSER
 * ----------------------------------------------
 * steel-agent.ts pairs a browser (Steel) with a planner loop we wrote and
 * maintain (`runAgentLoop`: screenshot, ask a model what to click, repeat).
 * Browser Use Cloud sells the whole agent — you post a task and collect the
 * result — so adopting it deletes the loop instead of porting it. Their
 * published numbers are 82% of 106 Internal Bench Hard tasks at $0.17 per solved
 * task, and 98% across Online-Mind2Web.
 *
 * THE TRADE SAM MADE KNOWINGLY (2026-08-23)
 * -----------------------------------------
 * The model runs on Browser Use's side, from their hosted list (Anthropic, xAI,
 * OpenAI, Google, MiniMax). Bedrock is NOT on that list. So this path is an
 * explicit, deliberate exception to "all Carrier AI goes through Bedrock":
 * browser-automation model spend leaves the AWS account and loses the
 * `requestMetadata` per-org attribution that @carrier/ai gives everything else.
 *
 * The alternative that would have kept Bedrock — browser-use's own
 * `ChatAWSBedrock` — exists only in the PYTHON library (now with a Rust core),
 * so it cannot run in a Worker. Keeping Bedrock AND dropping the loop would have
 * meant moving ui_agent execution off Cloudflare onto a container.
 *
 * WHY REST AND NOT THE SDK
 * ------------------------
 * `browser-use-sdk` does list Cloudflare Workers as a supported runtime, so it
 * would work. It is also ~1.18 MB unpacked. bedrock.ts records that
 * @aws-sdk/client-bedrock-runtime was rejected at roughly 1 MB and replaced with
 * aws4fetch plus a hand-written decoder; adding a comparable bundle here would
 * argue against that precedent for a client that is a few fetch calls.
 *
 * CONTRACT COMPATIBILITY
 * ----------------------
 * Everything here is shaped to ui-agent-types.ts, which is shaped to Manus's
 * schema from before Steel ("matches Manus UI_AGENT_RESULT_SCHEMA exactly",
 * `PENDING_ASK_PREFIX = "manus_pending_ask:"`). This repo has already done one
 * vendor migration by holding the interface still; this is the second, done the
 * same way, so call sites move one import at a time rather than in a single
 * irreversible commit.
 *
 * WHAT THE STRAIGHT CUTOVER GAVE UP
 * ---------------------------------
 * There is no Steel fallback. Sam chose that on 2026-08-23 over a two-provider
 * arrangement, on the grounds that a fallback nobody exercises is a code path
 * nobody has tested — and the failure it would have to survive is Browser Use
 * being down, which polling already reports honestly.
 *
 * The hosted agent also runs its own planner, so three things the loop did are
 * simply gone rather than ported: a screenshot count (there is none to report,
 * see `toTaskResult`), a step cap that maps onto anything (`max_steps` is now
 * advisory only), and a `request_input` callback fired from inside our own
 * process. The ask flow is therefore driven by an OBSERVED paused status on
 * poll rather than by a callback — see `writePendingAsk`.
 */

import {
  PENDING_ASK_PREFIX,
  PENDING_ASK_TTL_SECONDS,
  type SteelPendingAsk,
  type SteelTask,
  type SteelTaskResult,
  type SteelTaskStatus,
} from "./ui-agent-types.js";

// ---------------------------------------------------------------------------
// Runtime injection — why this module holds no binding
// ---------------------------------------------------------------------------

/**
 * Anything with KV's get/put/delete shape.
 *
 * Same injection precedent as `AiBudgetStore` in budget.ts, for the same
 * reason: this package declares itself binding-free so both the Worker and the
 * stdio CLI can call it. The Worker passes its `CARRIER_USERS` KV namespace
 * directly — Workers KV satisfies this structurally. stdio passes a Map-backed
 * double.
 *
 * `get` returns `string | null` rather than `unknown` (the looser type budget.ts
 * needs): every value this module stores is written by `JSON.stringify` in
 * `putTask`/`writePendingAsk` and read back through `JSON.parse`, so a store
 * that parsed on the caller's behalf would break the round-trip here rather
 * than silently miscount as it would in a meter.
 */
export interface UiAgentStore {
  get(key: string): Promise<string | null>;
  put(
    key: string,
    value: string,
    options?: { expirationTtl?: number },
  ): Promise<void>;
  delete(key: string): Promise<void>;
}

/**
 * One Analytics Engine row. Matches `writeDataPoint`'s argument so the Worker
 * can pass `env.AUDIT_LOG.writeDataPoint.bind(env.AUDIT_LOG)` unchanged, which
 * is what keeps blob POSITIONS identical across the extraction — see
 * `writeAgentAudit` below for why moving one would be a breaking change.
 *
 * A sink is required rather than optional: a caller with nowhere to write spend
 * should pass an explicit no-op and say so, not omit it and lose the only
 * record of what a run cost.
 */
export type UiAgentAuditSink = (row: {
  blobs: string[];
  doubles: number[];
  indexes: string[];
}) => void;

/**
 * Everything this module needs from its host, and nothing else.
 *
 * Replaces the Cloudflare `Env` this file used to take. The three fields are
 * exactly the couplings that existed before the move: the vendor key, the KV
 * namespace (5 call sites), and the Analytics Engine dataset (1 call site).
 */
export interface UiAgentEnv {
  /** Browser Use Cloud API key. Absent is a valid, configured-off state. */
  apiKey?: string;
  store: UiAgentStore;
  audit: UiAgentAuditSink;
}

/** V4 is the current interface for new integrations; v2/v3 are older surfaces. */
const BROWSER_USE_API = "https://api.browser-use.com/api/v4";

/**
 * Wall-clock ceiling for one hosted run, and the poll interval between checks.
 *
 * Polling rather than webhooks on purpose, for now. ~/CLAUDE.md records that v2
 * tasks fire webhooks and v4 runs send nothing (measured 2026-08-21), while the
 * current Browser Use docs list webhooks as a V4 feature. Those cannot both be
 * true. Until it is re-measured against this account, polling is the option that
 * works under either answer — and a webhook can be added later without changing
 * this module's contract.
 */
const RUN_TIMEOUT_MS = 10 * 60_000;
const POLL_INTERVAL_MS = 3_000;

/** Present only when the integration is configured. Absent is a valid state. */
export function browserUseConfigured(env: UiAgentEnv): boolean {
  return Boolean(env.apiKey);
}

function apiKey(env: UiAgentEnv): string {
  const key = env.apiKey;
  if (!key) {
    throw new Error(
      "BROWSER_USE_API_KEY is not set — the browser-use provider cannot run.",
    );
  }
  return key;
}

/**
 * Shape observed from a real V4 run on 2026-08-23, not inferred from docs.
 * Create returns: eventsUrl, id, missingFileIds, model, sessionId, status,
 * workspaceId. A finished run adds: attachedFileIds, contextLimit, createdAt,
 * error, flags, judgement, result, task, title, totalCostUsd, totalInputTokens,
 * totalOutputTokens, updatedAt.
 *
 * Note there is NO sessionUrl / liveUrl field — only `sessionId`. The viewer URL
 * has to be built from it.
 */
interface V4Run {
  id: string;
  status?: string;
  result?: unknown;
  output?: unknown;
  error?: string | null;
  sessionId?: string;
  eventsUrl?: string;
  /** Real spend for this run, reported by Browser Use. */
  totalCostUsd?: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
}

async function v4(
  env: UiAgentEnv,
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<{ ok: boolean; status: number; data: unknown }> {
  const res = await fetch(`${BROWSER_USE_API}${path}`, {
    method: init?.method ?? "GET",
    headers: {
      "X-Browser-Use-API-Key": apiKey(env),
      "Content-Type": "application/json",
    },
    ...(init?.body ? { body: JSON.stringify(init.body) } : {}),
  });
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    // A non-JSON body on an error status is still worth surfacing as a status.
  }
  return { ok: res.ok, status: res.status, data };
}

/**
 * Map Browser Use's run status onto the four states downstream code understands.
 *
 * Unknown statuses map to "running" rather than "error": a status string this
 * code has not seen before is far more likely to be a new intermediate state
 * than a failure, and reporting a live run as failed is the worse mistake — it
 * strands a browser session nobody polls again.
 */
export function mapStatus(raw: string | undefined): SteelTaskStatus {
  // Observed live: queued -> dispatching -> running -> completed. The first two
  // hit the default arm below, which is exactly why that arm returns "running"
  // rather than "error".
  switch ((raw ?? "").toLowerCase()) {
    case "finished":
    case "completed":
    case "success":
    case "stopped":
      return "stopped";
    case "failed":
    case "error":
      return "error";
    case "paused":
    case "waiting":
    case "needs_input":
      return "waiting";
    default:
      return "running";
  }
}

/**
 * Coerce a hosted run's output into SteelTaskResult.
 *
 * Deliberately forgiving. The hosted agent returns whatever the task asked for,
 * and a structured-output schema is optional, so this must not throw on a shape
 * it did not expect — a result that cannot be parsed is still a result the
 * caller should see, with success reflecting the run rather than the parse.
 */
/**
 * Scrub secrets out of agent-authored text before it is stored or returned.
 *
 * Callers that hold the portal credentials pass a redactor built from them (see
 * `redactCredentials` in ui-agent-prompts.ts). The default below runs even when
 * they do not, because the caller forgetting is the case that actually leaks.
 */
export type Redactor = (text: string) => string;

/**
 * Last-resort scrub for the shape the prompt itself uses.
 *
 * buildOcsPortalPrompt writes "  Password: <plaintext>\n", so a result that
 * quotes the instructions back carries that line verbatim. This cannot catch a
 * password mentioned in prose — only a credential-aware Redactor can — but it
 * costs nothing and covers the common echo.
 */
export function defaultRedact(text: string): string {
  return text
    .replace(/(\bpassword\b\s*[:=]\s*)\S+/gi, "$1***REDACTED***")
    .replace(/(\busername\b\s*[:=]\s*)\S+/gi, "$1***REDACTED***");
}

export function toTaskResult(run: V4Run, redact?: Redactor): SteelTaskResult {
  const status = mapStatus(run.status);
  const scrub = (text: string): string =>
    defaultRedact(redact ? redact(text) : text);
  const payload = (run.result ?? run.output) as
    | Record<string, unknown>
    | string
    | null
    | undefined;

  if (payload && typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    return {
      success: status === "stopped" && !run.error,
      summary: scrub(
        typeof p.summary === "string"
          ? p.summary
          : JSON.stringify(payload),
      ).slice(0, 2000),
      ...(typeof p.entity_id === "string" ? { entity_id: p.entity_id } : {}),
      ...(run.error ? { error_message: scrub(run.error) } : {}),
      // The hosted agent does not report a screenshot count. Zero is honest;
      // inventing one would put a fabricated number in an audit row.
      screenshots_taken: 0,
    };
  }

  return {
    success: status === "stopped" && !run.error,
    summary: typeof payload === "string" ? scrub(payload).slice(0, 2000) : "",
    ...(run.error ? { error_message: scrub(run.error) } : {}),
    screenshots_taken: 0,
  };
}

/**
 * Build the viewer URL from `sessionId`.
 *
 * A live run object carries no sessionUrl/liveUrl field — checked against a real
 * run rather than assumed from the docs, which is why the first version of this
 * function returned undefined every time.
 */
function viewerUrl(run: V4Run): string | undefined {
  return run.sessionId
    ? `https://cloud.browser-use.com/sessions/${run.sessionId}`
    : undefined;
}

/** Real spend for one run, when the API reports it. */
export interface RunSpend {
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
}

/**
 * Pull the spend Browser Use reports for a finished run.
 *
 * Worth having: moving this path off Bedrock gives up the `requestMetadata`
 * per-org attribution that @carrier/ai puts on every other model call, so the
 * run's own reported cost is the only per-call spend figure left. It is a real
 * billed number from the vendor rather than a rate-card estimate.
 */
export function runSpend(run: V4Run | null): RunSpend | null {
  if (!run || typeof run.totalCostUsd !== "number") return null;
  return {
    costUsd: run.totalCostUsd,
    inputTokens: run.totalInputTokens ?? 0,
    outputTokens: run.totalOutputTokens ?? 0,
  };
}

/**
 * Start a hosted run. Returns as soon as the run exists, so a caller can answer
 * its own request immediately and finish inside `waitUntil`.
 */
export async function createRun(
  env: UiAgentEnv,
  task: string,
  opts?: { model?: string; outputSchema?: Record<string, unknown> },
): Promise<{ runId: string; sessionViewerUrl?: string }> {
  const body: Record<string, unknown> = { task };
  if (opts?.model) body.model = opts.model;
  if (opts?.outputSchema) body.schema = opts.outputSchema;

  const res = await v4(env, "/runs", { method: "POST", body });
  if (!res.ok) {
    throw new Error(
      `Browser Use run create failed: HTTP ${res.status} ${JSON.stringify(res.data).slice(0, 300)}`,
    );
  }
  const run = res.data as V4Run;
  if (!run?.id) throw new Error("Browser Use run create returned no id");
  const url = viewerUrl(run);
  return { runId: run.id, ...(url ? { sessionViewerUrl: url } : {}) };
}

/** Read one run's current state, mapped onto the downstream task shape. */
export async function getRun(env: UiAgentEnv, runId: string): Promise<V4Run | null> {
  const res = await v4(env, `/runs/${encodeURIComponent(runId)}`);
  if (!res.ok) return null;
  return res.data as V4Run;
}

/**
 * Poll until the run leaves a running state, or the ceiling is hit.
 *
 * A timeout returns the last known state rather than throwing: the run is still
 * alive on Browser Use's side and `ui_agent_status` can pick it up again, so
 * turning a slow task into an exception would lose work that is still going.
 */
export async function waitForRun(
  env: UiAgentEnv,
  runId: string,
  timeoutMs = RUN_TIMEOUT_MS,
): Promise<V4Run | null> {
  const deadline = Date.now() + timeoutMs;
  let last: V4Run | null = null;
  while (Date.now() < deadline) {
    last = await getRun(env, runId);
    if (last && mapStatus(last.status) !== "running") return last;
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return last;
}

/**
 * Build the KV-shaped task record downstream code already stores and reads.
 * Same field names as steel-agent.ts so `ui_agent_status` needs no change.
 */
export function toSteelTask(
  taskId: string,
  run: V4Run | null,
  opts?: {
    title?: string;
    ownerSub?: string;
    createdAt?: string;
    /** Built from the caller's portal credentials. See Redactor. */
    redact?: Redactor;
  },
): SteelTask {
  const now = new Date().toISOString();
  const status = mapStatus(run?.status);
  const url = run ? viewerUrl(run) : undefined;
  return {
    task_id: taskId,
    status,
    ...(opts?.ownerSub ? { owner_sub: opts.ownerSub } : {}),
    // The console's task detail view renders steel_session_id. It is the
    // provider's session id, whoever the provider is.
    ...(run?.sessionId ? { steel_session_id: run.sessionId } : {}),
    ...(run?.id ? { run_id: run.id } : {}),
    ...(url ? { session_viewer_url: url } : {}),
    ...(opts?.title ? { title: opts.title } : {}),
    created_at: opts?.createdAt ?? now,
    updated_at: now,
    ...(run && status !== "running"
      ? { result: toTaskResult(run, opts?.redact) }
      : {}),
    // Scrubbed on the same grounds as the result: the question is agent-authored
    // text, and an agent that pauses to ask for help is exactly the one likely
    // to quote its own instructions — password line included — while explaining
    // what it is stuck on. This string is stored in KV and handed to operators
    // by ui_agent_list_pending.
    ...(status === "waiting"
      ? { pending_question: pendingQuestion(run, opts?.redact) }
      : {}),
  };
}

/**
 * The question a paused run is waiting on, when it names one.
 *
 * Falls back to a generic prompt rather than an empty string: an operator
 * looking at `ui_agent_list_pending` needs to know a run wants something, even
 * when the provider did not say what.
 */
function pendingQuestion(run: V4Run | null, redact?: Redactor): string {
  const payload = run?.result as Record<string, unknown> | undefined;
  const q = payload && typeof payload === "object" ? payload.question : undefined;
  if (typeof q !== "string" || q.length === 0) {
    return "The browser agent is paused and needs input to continue.";
  }
  return defaultRedact(redact ? redact(q) : q);
}

// ===========================================================================
// Dispatch contract
//
// The five functions below are what the six call sites import. They were
// steel-agent.ts's exports; the names lost their vendor, the behaviour did not,
// with the two deliberate differences noted at each one.
// ===========================================================================

/** KV key for a task record. Byte-identical to what the console lists. */
function taskKey(taskId: string): string {
  return `steel_task:${taskId}`;
}

/** Seven days, matching what the console tells operators tasks live for. */
const TASK_TTL_SECONDS = 7 * 24 * 3600;

export interface UiAgentDispatchResult {
  task_id: string;
  status: SteelTaskStatus;
  session_viewer_url?: string;
  result?: SteelTaskResult;
  /** The vendor's own billed figure, once the run is finished. */
  spend?: RunSpend;
}

/** Handle returned by {@link beginUiAgentRun} once the run exists and KV is written. */
export interface UiAgentBeginHandle {
  task_id: string;
  session_viewer_url?: string;
  /** Polls the run to completion and persists the final KV record. */
  complete: () => Promise<UiAgentDispatchResult>;
}

export interface UiAgentRunOptions {
  /** Caller-supplied id. The scheduler uses it to make a fired window idempotent. */
  taskId?: string;
  outputSchema?: Record<string, unknown>;
  /**
   * Advisory only now. The hosted agent runs its own planner and takes no step
   * cap, so this no longer bounds anything — it is kept because callers still
   * price a task from it, and dropping the parameter would silently change
   * every cost estimate to one computed from a default.
   */
  maxSteps?: number;
  /** Carrier user id — stored on the KV task for `ui_agent_status` ownership checks. */
  ownerSub?: string;
  /** Model override from Browser Use's hosted list. Omitted means their default. */
  model?: string;
  /**
   * Scrubs secrets from agent-authored text before it reaches KV.
   *
   * Callers that paste portal credentials into the prompt MUST pass one, built
   * from `redactCredentials`. `defaultRedact` runs regardless and covers the
   * literal "Password: <value>" shape the prompt writes, but only a
   * credential-aware redactor catches the value quoted in prose.
   */
  redact?: Redactor;
}

/**
 * Start a run and write the KV record, then hand back `complete()`.
 *
 * Split this way so a Worker can answer its own request inside the 30s wall
 * clock and finish the run in `waitUntil` — the same shape the planner loop
 * needed, for the same reason.
 */
export async function beginUiAgentRun(
  prompt: string,
  title: string,
  env: UiAgentEnv,
  opts?: UiAgentRunOptions,
): Promise<UiAgentBeginHandle> {
  if (!browserUseConfigured(env)) {
    throw new Error("BROWSER_USE_API_KEY is not configured");
  }

  const taskId =
    opts?.taskId ?? `bu_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const createdAt = new Date().toISOString();

  const base: SteelTask = {
    task_id: taskId,
    status: "running",
    title,
    created_at: createdAt,
    updated_at: createdAt,
    ...(opts?.ownerSub ? { owner_sub: opts.ownerSub } : {}),
  };

  // Written before the run is created on purpose. If create throws, the caller
  // still has a record to look at rather than a task id that resolves to
  // nothing — the same order the Steel path used.
  await putTask(env, base);

  let created: { runId: string; sessionViewerUrl?: string };
  try {
    created = await createRun(env, prompt, {
      ...(opts?.model ? { model: opts.model } : {}),
      ...(opts?.outputSchema ? { outputSchema: opts.outputSchema } : {}),
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    await putTask(env, {
      ...base,
      status: "error",
      updated_at: new Date().toISOString(),
      result: {
        success: false,
        summary: `Failed to start a Browser Use run: ${message}`,
        error_message: message,
        screenshots_taken: 0,
      },
    });
    throw err;
  }

  const dispatched: SteelTask = {
    ...base,
    run_id: created.runId,
    ...(created.sessionViewerUrl
      ? { session_viewer_url: created.sessionViewerUrl }
      : {}),
    updated_at: new Date().toISOString(),
  };
  await putTask(env, dispatched);
  await bumpUsageCounter(env);

  const complete = async (): Promise<UiAgentDispatchResult> => {
    let run: V4Run | null;
    try {
      run = await waitForRun(env, created.runId);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const failed: SteelTaskResult = {
        success: false,
        summary: `Browser Use run failed: ${message}`,
        error_message: message,
        screenshots_taken: 0,
      };
      await putTask(env, {
        ...dispatched,
        status: "error",
        result: failed,
        updated_at: new Date().toISOString(),
      });
      writeAgentAudit(env, {
        event_type: "task_stopped",
        stop_reason: "error",
        task_id: taskId,
      });
      return {
        task_id: taskId,
        status: "error",
        ...(created.sessionViewerUrl
          ? { session_viewer_url: created.sessionViewerUrl }
          : {}),
        result: failed,
      };
    }

    const final = toSteelTask(taskId, run, {
      title,
      ...(opts?.ownerSub ? { ownerSub: opts.ownerSub } : {}),
      ...(opts?.redact ? { redact: opts.redact } : {}),
      createdAt,
    });
    // waitForRun returns the last known state on timeout, so a run still going
    // keeps its dispatch-time viewer URL rather than losing it.
    if (!final.session_viewer_url && created.sessionViewerUrl) {
      final.session_viewer_url = created.sessionViewerUrl;
    }
    final.run_id = created.runId;
    await putTask(env, final);

    if (final.status === "waiting") {
      await writePendingAsk(env, final);
    }

    const spend = runSpend(run);
    writeAgentAudit(env, {
      event_type: "task_stopped",
      stop_reason:
        final.status === "waiting"
          ? "ask"
          : final.status === "stopped"
            ? "finish"
            : final.status === "running"
              ? "timeout"
              : "error",
      task_id: taskId,
      ...(spend ? { spend } : {}),
    });

    return {
      task_id: taskId,
      status: final.status,
      ...(final.session_viewer_url
        ? { session_viewer_url: final.session_viewer_url }
        : {}),
      ...(final.result ? { result: final.result } : {}),
      ...(spend ? { spend } : {}),
    };
  };

  return {
    task_id: taskId,
    ...(created.sessionViewerUrl
      ? { session_viewer_url: created.sessionViewerUrl }
      : {}),
    complete,
  };
}

/** Start a run and wait for it. Convenience wrapper over {@link beginUiAgentRun}. */
export async function runUiAgent(
  prompt: string,
  title: string,
  env: UiAgentEnv,
  opts?: UiAgentRunOptions,
): Promise<UiAgentDispatchResult> {
  const handle = await beginUiAgentRun(prompt, title, env, opts);
  return await handle.complete();
}

/** Read a task record. Returns null for an id that has expired or never existed. */
export async function getUiAgentTask(
  taskId: string,
  env: UiAgentEnv,
): Promise<SteelTask | null> {
  const raw = await env.store.get(taskKey(taskId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SteelTask;
  } catch {
    return null;
  }
}

/**
 * Answer a paused task's question.
 *
 * Clears the pending-ask entry and puts the task back to running. It does NOT
 * push the reply into the live run, and neither did the Steel path it replaces
 * — that one carried a TODO saying resume-from-checkpoint needs the message
 * history persisted. The behaviour is unchanged on purpose: a cutover is the
 * wrong place to also fix a gap, and pretending to deliver a 2FA code that
 * never reaches the browser is worse than the current honest no-op.
 */
export async function resumeUiAgentTask(
  taskId: string,
  reply: string,
  env: UiAgentEnv,
): Promise<{ ok: boolean; error?: string }> {
  const pendingRaw = await env.store.get(`${PENDING_ASK_PREFIX}${taskId}`);
  if (!pendingRaw) {
    return { ok: false, error: "No pending-ask entry found for this task_id." };
  }

  const task = await getUiAgentTask(taskId, env);
  if (!task) {
    return { ok: false, error: "Task record not found." };
  }

  await env.store.delete(`${PENDING_ASK_PREFIX}${taskId}`);

  const { pending_question: _dropped, ...rest } = task;
  await putTask(env, {
    ...rest,
    status: "running",
    updated_at: new Date().toISOString(),
  });

  writeAgentAudit(env, {
    event_type: "task_stopped",
    stop_reason: "resumed",
    task_id: taskId,
    replyLen: reply.length,
  });

  return { ok: true };
}

// ---------------------------------------------------------------------------
// KV helpers
// ---------------------------------------------------------------------------

async function putTask(env: UiAgentEnv, task: SteelTask): Promise<void> {
  await env.store.put(taskKey(task.task_id), JSON.stringify(task), {
    expirationTtl: TASK_TTL_SECONDS,
  });
}

/** Days a `steel_usage:<YYYY-MM>` key outlives the month it counts, then expires unread. */
const USAGE_COUNTER_TTL_SECONDS = 35 * 24 * 3600;

/**
 * Bump the calendar-month dispatch counter `ui_agent_usage` reads.
 *
 * Called once per confirmed dispatch (after `createRun` succeeds), from the
 * single choke point every `ui_*` tool and the schedule cron go through, so
 * this is the only place that needs to count. Plain-integer string, not JSON:
 * `ui_agent_usage` in tools-ui-agent-schedule.ts reads this key with
 * `parseInt(raw, 10)`, and changing the format there would be a second,
 * unrelated migration.
 *
 * Read-then-write like `bumpUsed` in budget.ts, and for the same reason: KV
 * has no atomic increment, and a `ui_agent_usage` estimate is not a billing
 * ledger — a rare concurrent-dispatch undercount costs nothing. Best-effort:
 * a KV failure here must not fail the dispatch it is only counting.
 */
async function bumpUsageCounter(env: UiAgentEnv, now: Date = new Date()): Promise<void> {
  const monthKey = `steel_usage:${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  const raw = await env.store.get(monthKey).catch(() => null);
  const count = raw ? parseInt(raw, 10) || 0 : 0;
  await env.store
    .put(monthKey, String(count + 1), { expirationTtl: USAGE_COUNTER_TTL_SECONDS })
    .catch(() => undefined);
}

/**
 * Record that a run is waiting, so `ui_agent_list_pending` and `ui_agent_reply`
 * still see it.
 *
 * The loop wrote this from a `request_input` tool call fired inside our own
 * process. There is no such callback from a hosted agent, so the trigger is now
 * an observed paused status on poll. Same KV key and same value shape, so the
 * ask tools and the console need no change.
 */
async function writePendingAsk(env: UiAgentEnv, task: SteelTask): Promise<void> {
  const entry: SteelPendingAsk = {
    task_id: task.task_id,
    question:
      task.pending_question ??
      "The browser agent is paused and needs input to continue.",
    task_url: task.session_viewer_url ?? "",
    asked_at: new Date().toISOString(),
  };
  await env.store.put(
    `${PENDING_ASK_PREFIX}${task.task_id}`,
    JSON.stringify(entry),
    { expirationTtl: PENDING_ASK_TTL_SECONDS },
  );
}

// ---------------------------------------------------------------------------
// Analytics Engine audit writes
// ---------------------------------------------------------------------------

interface AgentAuditEntry {
  event_type: string;
  stop_reason: string;
  task_id: string;
  /** Real vendor-billed spend, when the run reported it. */
  spend?: RunSpend;
  replyLen?: number;
}

/**
 * One audit row per finished run, now carrying what the run actually cost.
 *
 * WHY THE POSITIONS LOOK LIKE THIS
 * --------------------------------
 * See audit-schema.ts: blob and double POSITIONS are the query contract, so
 * every field is APPENDED past what already exists and nothing moves. blob1..5
 * are exactly what the Steel writer emitted, blob1 included — the literal
 * "steel_agent" is kept because a dashboard filtering on it would otherwise
 * lose every row from the day of the cutover onward, and losing history is a
 * worse outcome than a stale label.
 *
 * `screenshots_taken` at blob5 is now always "0": the hosted agent does not
 * report one, and a fabricated count in an audit row is worse than an honest
 * zero. The slot stays rather than being reused for something else, because
 * reuse is the reorder this file exists to prevent.
 *
 * doubles[0] is the pre-existing question/reply length. Cost and token counts
 * are appended after it. That is the point of wiring this at all: moving off
 * Bedrock gave up `requestMetadata` per-org attribution, and this row is the
 * only place the per-call figure survives.
 */
function writeAgentAudit(env: UiAgentEnv, entry: AgentAuditEntry): void {
  try {
    env.audit({
      blobs: [
        "steel_agent",
        entry.event_type,
        entry.stop_reason,
        entry.task_id,
        "0",
        "browser_use",
      ],
      doubles: [
        entry.replyLen ?? 0,
        entry.spend?.costUsd ?? 0,
        entry.spend?.inputTokens ?? 0,
        entry.spend?.outputTokens ?? 0,
      ],
      indexes: [entry.task_id],
    });
  } catch (err) {
    console.error(`[browser-use-agent] audit write failed: ${(err as Error).message}`);
  }
}
